import { Alert, AlertTitle, Button, CircularProgress, Stack, Typography } from "@mui/material"
import { UseMutationResult, UseQueryResult } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { PropsWithChildren, ReactNode } from "react"

export type QueryDisplayProps = {
    queries?: UseQueryResult[],
    mutations?: UseMutationResult[],
    ignoreLoading?: boolean,
    loadingComponent?: ReactNode,
} & PropsWithChildren

export function QueryAlert({
    queries,
    mutations,
    ignoreLoading,
    loadingComponent,
    children
}: QueryDisplayProps) {

    const errorQueries = queries?.filter(x => x.isError) || [];
    if (errorQueries.length > 0) {
        let errorTitle = "An unexpected error occured";
        let errorMessage = "If the problem persists, please contact our support team."

        if (errorQueries.some(x => x.error instanceof AxiosError && x.error.status === 403)) {
            errorTitle = "You are not authorized to access this resource.";
            errorMessage = "Please contact a user manager to request access.";
        }
        return <Stack spacing={1} margin={2}>
            <Alert severity="error" action={
                <Button color="inherit" size="small" onClick={() => errorQueries.forEach(x => x.refetch())}>
                    Retry
                </Button>
            }>
                <AlertTitle>{errorTitle}</AlertTitle>
                <Typography variant='body2' marginTop={1} fontStyle={'italic'}>
                    {errorMessage}
                </Typography>
            </Alert>
        </Stack>
    }

    const fetchingQueries = queries?.filter(x => x.isFetching || x.isPaused || x.isLoading) || [];

    if (fetchingQueries.length > 0 && !ignoreLoading)
        if (loadingComponent)
            return <>{loadingComponent}</>
        else
            return <Stack alignItems="center" justifyContent="center" height="100%" margin={8}>
                <CircularProgress size={60} />
            </Stack>

    return <>
        <MutationAlert />
        {children}
    </>

    function MutationAlert() {
        const errorMutations = mutations?.filter(x => x.isError) || [];
        if (errorMutations.length === 0)
            return null;

        let errorTitle = "An unexpected error occured";
        let errorMessage = "If the problem persists, please contact our support team."

        if (errorMutations.some(x => x.error instanceof AxiosError && x.error.status === 403)) {
            errorTitle = "You are not authorized to perform this action.";
            errorMessage = "Please contact a user manager to request access.";
        }

        return <Stack spacing={1} margin={2}>
            <Alert severity="error">
                <AlertTitle>{errorTitle}</AlertTitle>
                <Typography variant='body2' marginTop={1} fontStyle={'italic'}>
                    {errorMessage}
                </Typography>
            </Alert>
        </Stack>
    }
}